<template>
  <div class="space-y-3">
    <!-- Header bar -->
    <div class="flex justify-between items-center">
      <div class="flex items-center gap-3">
        <h3 class="text-sm font-semibold text-growth-900">Primary Care Model</h3>
        <span v-if="data" :class="['text-xs px-2 py-0.5 rounded-full font-mono', qualityClass]">{{ data.data_quality }} — {{ providedCount }}/{{ totalCount }} inputs provided</span>
      </div>
      <div v-if="data && data.practice_context" class="text-xs text-growth-600 font-mono">
        {{ data.practice_context.practice_name }}
        <span v-if="data.practice_context.tin_id" class="ml-2">TIN {{ data.practice_context.tin_id }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="bg-white rounded-lg border border-growth-50 p-8 text-center text-growth-600">
      <svg class="animate-spin h-6 w-6 mx-auto mb-2" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
      Running primary care model...
    </div>

    <!-- Error state -->
    <div v-else-if="data && data.error" class="bg-white rounded-lg border border-red-200 overflow-hidden">
      <div class="px-4 py-2 bg-red-50 border-b border-red-200">
        <span class="text-xs font-mono font-semibold text-red-800">MODEL API ERROR</span>
      </div>
      <div class="p-4 font-mono text-sm text-red-700">{{ data.error }}</div>
      <div v-if="data.inputs_sent" class="p-4 border-t border-red-100">
        <div class="text-xs text-growth-600 mb-2">Inputs that were assembled ({{ Object.keys(data.inputs_sent).length }} fields):</div>
        <div class="font-mono text-xs space-y-0.5">
          <div v-for="(val, key) in data.inputs_sent" :key="key"><span class="text-growth-600">{{ key }}:</span> {{ val }}</div>
        </div>
      </div>
    </div>

    <!-- Insufficient data -->
    <div v-else-if="data && data.insufficient_data" class="bg-white rounded-lg border border-growth-50 p-6 text-center">
      <div class="text-growth-600 text-sm mb-2">Insufficient data to run model</div>
      <div class="text-xs text-growth-400">{{ data.reason }}</div>
      <div v-if="data.practice_context" class="mt-3 text-xs text-growth-400 font-mono">
        {{ data.practice_context.practice_name }}
        <span v-if="data.practice_context.tin_id" class="ml-2">TIN {{ data.practice_context.tin_id }}</span>
        <span v-else class="ml-2">No TIN linkage</span>
      </div>
    </div>

    <!-- Model view -->
    <template v-else-if="data && data.results">

      <!-- STEP 1: Practice Profile -->
      <div v-if="profile" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('profile')">
          <span class="text-xs font-mono font-semibold">STEP 1: PRACTICE PROFILE</span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.profile ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.profile" class="p-4 font-mono text-sm">
          <table class="w-full">
            <tbody>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 w-52">Physicians</td><td class="py-1.5 font-semibold">{{ profile.physician_count }}</td><td class="py-1.5 text-right">{{ traceTag('practice_profile', 'physician_count') }}</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">APPs</td><td class="py-1.5 font-semibold">{{ profile.app_count }}</td><td class="py-1.5 text-right">{{ traceTag('practice_profile', 'app_count') }}</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Total Providers</td><td class="py-1.5 font-semibold">{{ profile.total_providers }}</td><td class="py-1.5 text-right text-xs text-growth-400">derived</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Admin Staff</td><td class="py-1.5">{{ profile.admin_count }}</td><td class="py-1.5 text-right text-xs text-growth-400">providers × 0.5</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Clinical Staff</td><td class="py-1.5">{{ profile.clinical_staff_count }}</td><td class="py-1.5 text-right text-xs text-growth-400">providers × 2.0</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Total Staff</td><td class="py-1.5 font-semibold">{{ profile.total_staff }}</td><td></td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">State</td><td class="py-1.5">{{ profile.state || '—' }}</td><td class="py-1.5 text-right">{{ traceTag('practice_profile', 'state') }}</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Care Setting</td><td class="py-1.5">{{ profile.care_setting }}</td><td class="py-1.5 text-right">{{ traceTag('practice_profile', 'care_setting') }}</td></tr>
              <tr><td class="py-1.5 text-growth-600">Location</td><td class="py-1.5">{{ profile.location_type }}</td><td class="py-1.5 text-right">{{ traceTag('practice_profile', 'location_type') }}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- STEP 2: Patient Panel -->
      <div v-if="panel" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('panel')">
          <span class="text-xs font-mono font-semibold">STEP 2: PATIENT PANEL</span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.panel ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.panel" class="p-4 font-mono text-sm">
          <table class="w-full mb-4">
            <tbody>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 w-52">Annual Visits</td><td class="py-1.5 font-semibold">{{ fmtNum(panel.annual_visits) }}</td><td class="py-1.5 text-right">{{ traceTag('patient_panel', 'annual_visits') }}</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Total Patients</td><td class="py-1.5 font-semibold">{{ fmtNum(panel.total_patients) }}</td><td class="py-1.5 text-right text-xs text-growth-400">derived</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Attributed Benes</td><td class="py-1.5 font-semibold">{{ fmtNum(panel.attributed_benes) }}</td><td class="py-1.5 text-right">{{ traceTag('patient_panel', 'attributed_benes') }}</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Visits / Provider</td><td class="py-1.5">{{ fmtNum(panel.visits_per_provider) }}</td><td></td></tr>
              <tr><td class="py-1.5 text-growth-600">Patients / Provider</td><td class="py-1.5">{{ fmtNum(panel.patients_per_provider) }}</td><td></td></tr>
            </tbody>
          </table>

          <!-- Payer breakdown -->
          <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Payer Breakdown</div>
          <table class="w-full text-xs">
            <thead>
              <tr class="text-left text-growth-600 uppercase border-b border-growth-50">
                <th class="pb-1.5 pr-4">Payer</th>
                <th class="pb-1.5 pr-4 text-right">Mix %</th>
                <th class="pb-1.5 pr-4 text-right">Patients</th>
                <th class="pb-1.5 pr-4 text-right">Visits</th>
                <th class="pb-1.5 pr-4 text-right">Visit Mix</th>
                <th class="pb-1.5 text-right">Source</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in payerOrder" :key="p" class="border-b border-growth-50">
                <td class="py-1.5 pr-4 capitalize">{{ p.replace('_', ' ') }}</td>
                <td class="py-1.5 pr-4 text-right font-semibold">{{ panel.payer[p]?.pct != null ? panel.payer[p].pct + '%' : '—' }}</td>
                <td class="py-1.5 pr-4 text-right">{{ fmtNum(panel.payer[p]?.patients) }}</td>
                <td class="py-1.5 pr-4 text-right">{{ fmtNum(panel.payer[p]?.visits) }}</td>
                <td class="py-1.5 pr-4 text-right">{{ panel.payer[p]?.visit_mix != null ? panel.payer[p].visit_mix + '%' : '—' }}</td>
                <td class="py-1.5 text-right">{{ traceTag('patient_panel', p + '_pct') }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Derivation note -->
          <div v-if="traceVal('patient_panel', 'annual_visits') === 'default'" class="mt-3 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
            annual_visits derived: {{ profile?.total_providers }} providers × {{ traceVal('patient_panel', 'daily_provider_panel') === 'default' ? '20' : '' }} patients/day × 5 days × 48 weeks = {{ fmtNum(panel.annual_visits) }}
          </div>
        </div>
      </div>

      <!-- STEP 3: FFS REVENUE -->
      <div v-if="ffs" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('ffs')">
          <span class="text-xs font-mono font-semibold">STEP 3: FFS REVENUE</span>
          <span :class="['text-xs px-2 py-0.5 rounded ml-2', pathClass(ffs.revenue_source)]">{{ ffs.revenue_source }}</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.ffs ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.ffs" class="p-4 font-mono text-sm">
          <!-- Summary -->
          <div class="bg-growth-10 rounded p-3 mb-4 text-center">
            <div class="text-xs text-growth-600 mb-1">Net Collections</div>
            <div class="text-2xl font-bold text-growth-900">{{ fmtDollar(ffs.net_collections) }}</div>
            <div class="text-xs text-growth-600 mt-1">{{ fmtDollar(ffs.revenue_per_visit) }} / visit</div>
          </div>

          <!-- Path: partial_actuals breakdown -->
          <template v-if="ffs.revenue_source === 'partial_actuals'">
            <table class="w-full">
              <tbody>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 w-52">Medicare Revenue</td><td class="py-1.5 font-semibold">{{ fmtDollar(ffs.medicare_revenue) }}</td><td class="py-1.5 text-right text-xs text-emerald-600">actual</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Medicaid Revenue</td><td class="py-1.5">{{ fmtDollar(ffs.medicaid_revenue) }}</td><td class="py-1.5 text-right text-xs" :class="ffs.medicaid_revenue ? 'text-emerald-600' : 'text-amber-600'">{{ ffs.medicaid_revenue ? 'actual' : 'not provided' }}</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Commercial Revenue (est)</td><td class="py-1.5">{{ fmtDollar(ffs.commercial_revenue_est) }}</td><td class="py-1.5 text-right text-xs text-amber-600">waterfall estimate</td></tr>
                <tr><td class="py-1.5 text-growth-600">Self-Pay Revenue (est)</td><td class="py-1.5">{{ fmtDollar(ffs.self_pay_revenue_est) }}</td><td class="py-1.5 text-right text-xs text-amber-600">waterfall estimate</td></tr>
              </tbody>
            </table>
          </template>

          <!-- Path: waterfall breakdown -->
          <template v-if="ffs.revenue_source === 'waterfall' && ffs.by_payer">
            <table class="w-full mb-3">
              <tbody>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 w-52">Gross Charges</td><td class="py-1.5">{{ fmtDollar(ffs.gross_charges) }}</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Adjusted Charges</td><td class="py-1.5">{{ fmtDollar(ffs.adjusted_charges) }}</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Collection Rate</td><td class="py-1.5">{{ ffs.collection_rate != null ? ffs.collection_rate + '%' : '—' }}</td></tr>
                <tr><td class="py-1.5 text-growth-600 font-semibold">Net Collections</td><td class="py-1.5 font-semibold">{{ fmtDollar(ffs.net_collections) }}</td></tr>
              </tbody>
            </table>
            <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">By Payer</div>
            <table class="w-full text-xs">
              <thead>
                <tr class="text-left text-growth-600 uppercase border-b border-growth-50">
                  <th class="pb-1.5 pr-4">Payer</th>
                  <th class="pb-1.5 pr-4 text-right">Visits</th>
                  <th class="pb-1.5 pr-4 text-right">Gross</th>
                  <th class="pb-1.5 pr-4 text-right">Adjusted</th>
                  <th class="pb-1.5 text-right">Net Collections</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in payerOrder" :key="p" class="border-b border-growth-50">
                  <td class="py-1.5 pr-4 capitalize">{{ p.replace('_', ' ') }}</td>
                  <td class="py-1.5 pr-4 text-right">{{ fmtNum(ffs.by_payer[p]?.visits) }}</td>
                  <td class="py-1.5 pr-4 text-right">{{ fmtDollar(ffs.by_payer[p]?.gross_charges) }}</td>
                  <td class="py-1.5 pr-4 text-right">{{ fmtDollar(ffs.by_payer[p]?.adjusted_charges) }}</td>
                  <td class="py-1.5 text-right font-semibold">{{ fmtDollar(ffs.by_payer[p]?.net_collections) }}</td>
                </tr>
              </tbody>
            </table>
          </template>

          <!-- Path: total_override -->
          <div v-if="ffs.revenue_source === 'total_override'" class="text-xs text-growth-600">
            User provided total FFS revenue directly. No waterfall computed.
          </div>
        </div>
      </div>

      <!-- STEP 4: VBC REVENUE -->
      <div v-if="vbc" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('vbc')">
          <span class="text-xs font-mono font-semibold">STEP 4: VBC REVENUE</span>
          <span :class="['text-xs px-2 py-0.5 rounded ml-2', pathClass(vbc.vbc_source)]">{{ vbc.vbc_source }}</span>
          <span v-if="vbc.in_aco" class="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded ml-2">IN ACO</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.vbc ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.vbc" class="p-4 font-mono text-sm">
          <!-- Tier 1: actual -->
          <template v-if="vbc.vbc_source === 'actual'">
            <div class="bg-emerald-50 rounded p-3 text-center">
              <div class="text-xs text-emerald-700 mb-1">Actual Earned Savings</div>
              <div class="text-2xl font-bold text-emerald-700">{{ fmtDollar(vbc.vbc_revenue) }}</div>
            </div>
          </template>

          <!-- Tier 2: rich -->
          <template v-if="vbc.vbc_source === 'rich'">
            <!-- Risk adjustment formula -->
            <div class="bg-growth-10 rounded p-3 mb-4 text-center">
              <div class="text-xs text-growth-600 mb-1">risk_adj_benchmark = benchmark × (roster_hcc ÷ regional_hcc)</div>
              <div class="text-lg" v-if="vbc.risk_ratio">
                ${{ fmtExact(data.practice_context?.inputs_sent?.benchmark_pmpy) }}
                <span class="text-growth-600 mx-1">×</span>
                ({{ data.practice_context?.inputs_sent?.weighted_hcc }}
                <span class="text-growth-600 mx-1">÷</span>
                {{ data.practice_context?.inputs_sent?.regional_hcc }})
                <span class="text-growth-600 mx-2">=</span>
                <span class="font-bold">${{ fmtExact(vbc.benchmark_pmpy) }}</span>
              </div>
            </div>

            <table class="w-full mb-4">
              <tbody>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 w-52">Weighted PMPY</td><td class="py-1.5 font-semibold">${{ fmtExact(vbc.weighted_pmpy) }}</td><td class="py-1.5 text-right">{{ traceTag('vbc_revenue', 'weighted_pmpy') }}</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Risk-Adj Benchmark</td><td class="py-1.5 font-semibold">${{ fmtExact(vbc.benchmark_pmpy) }}</td><td class="py-1.5 text-right text-xs text-growth-400">derived</td></tr>
                <tr v-if="vbc.risk_ratio" class="border-b border-growth-50">
                  <td class="py-1.5 text-growth-600">Risk Ratio</td>
                  <td class="py-1.5 font-semibold" :class="vbc.risk_ratio < 1 ? 'text-amber-600' : ''">
                    {{ vbc.risk_ratio }}×
                    <span class="text-xs font-normal text-growth-400">{{ vbc.risk_ratio < 1 ? '(healthier → benchmark reduced)' : '(sicker → benchmark increased)' }}</span>
                  </td>
                  <td></td>
                </tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Total Expenditure</td><td class="py-1.5">${{ fmtExact(vbc.total_expenditure) }}</td><td class="py-1.5 text-right text-xs text-growth-400">pmpy × benes</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Total Benchmark</td><td class="py-1.5">${{ fmtExact(vbc.total_benchmark) }}</td><td class="py-1.5 text-right text-xs text-growth-400">risk_adj × benes</td></tr>

                <!-- Savings/Loss formula -->
                <tr class="border-b border-growth-50 bg-growth-10">
                  <td class="py-1.5 text-growth-600">Savings</td>
                  <td class="py-1.5 font-semibold" :class="vbc.savings_amount >= 0 ? 'text-emerald-600' : 'text-red-600'">
                    ${{ fmtExact(vbc.savings_amount) }}
                  </td>
                  <td class="py-1.5 text-right font-semibold" :class="vbc.savings_pct >= 0 ? 'text-emerald-600' : 'text-red-600'">{{ vbc.savings_pct != null ? vbc.savings_pct + '%' : '—' }}</td>
                </tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Track</td><td class="py-1.5">{{ vbc.aco_track }}</td><td class="py-1.5 text-right">{{ traceTag('vbc_revenue', 'aco_track') }}</td></tr>
                <tr class="border-b border-growth-50">
                  <td class="py-1.5 text-growth-600">Shared Savings</td>
                  <td class="py-1.5 font-bold text-lg" :class="vbc.shared_savings > 0 ? 'text-emerald-600' : 'text-growth-600'">{{ fmtDollar(vbc.shared_savings) }}</td>
                  <td></td>
                </tr>
                <tr v-if="vbc.loss_exposure > 0" class="bg-red-50">
                  <td class="py-1.5 text-red-700 font-semibold">Loss Exposure</td>
                  <td class="py-1.5 text-red-700 font-semibold">${{ fmtExact(vbc.loss_exposure) }}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <!-- Scenarios -->
            <div v-if="vbc.scenarios" class="mb-2">
              <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Savings Scenarios</div>
              <table class="w-full text-xs">
                <thead>
                  <tr class="text-left text-growth-600 uppercase border-b border-growth-50">
                    <th class="pb-1.5 pr-4">Savings %</th>
                    <th class="pb-1.5 pr-4 text-right">Gross Savings</th>
                    <th class="pb-1.5 text-right">Shared Savings</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in vbc.scenarios" :key="s.savings_pct" class="border-b border-growth-50">
                    <td class="py-1.5 pr-4">{{ (s.savings_pct * 100).toFixed(0) }}%</td>
                    <td class="py-1.5 pr-4 text-right">{{ fmtDollar(s.gross_savings) }}</td>
                    <td class="py-1.5 text-right font-semibold">{{ fmtDollar(s.shared_savings) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- Tier 3: simple -->
          <template v-if="vbc.vbc_source === 'simple'">
            <div class="bg-growth-10 rounded p-3 mb-4 text-center">
              <div class="text-xs text-growth-600 mb-1">Estimated VBC Revenue (from assumptions)</div>
              <div class="text-2xl font-bold text-growth-900">{{ fmtDollar(vbc.vbc_revenue) }}</div>
            </div>
            <div v-if="vbc.by_payer" class="text-xs text-growth-600 uppercase tracking-wide mb-2">By Payer</div>
            <table v-if="vbc.by_payer" class="w-full text-xs">
              <thead>
                <tr class="text-left text-growth-600 uppercase border-b border-growth-50">
                  <th class="pb-1.5 pr-4">Payer</th>
                  <th class="pb-1.5 pr-4 text-right">Attributed</th>
                  <th class="pb-1.5 pr-4 text-right">Total Cost</th>
                  <th class="pb-1.5 pr-4 text-right">Target Savings</th>
                  <th class="pb-1.5 text-right">Practice Share</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in ['medicare', 'medicaid', 'commercial']" :key="p" class="border-b border-growth-50">
                  <td class="py-1.5 pr-4 capitalize">{{ p }}</td>
                  <td class="py-1.5 pr-4 text-right">{{ fmtNum(vbc.by_payer[p]?.attributed_patients) }}</td>
                  <td class="py-1.5 pr-4 text-right">{{ fmtDollar(vbc.by_payer[p]?.total_cost) }}</td>
                  <td class="py-1.5 pr-4 text-right">{{ fmtDollar(vbc.by_payer[p]?.target_savings) }}</td>
                  <td class="py-1.5 text-right font-semibold">{{ fmtDollar(vbc.by_payer[p]?.practice_share) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>

      <!-- STEP 5: ACCESS MODEL -->
      <div v-if="access" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('access')">
          <span class="text-xs font-mono font-semibold">STEP 5: ACCESS MODEL</span>
          <span :class="['text-xs px-2 py-0.5 rounded ml-2', access.access_source === 'provided' ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white']">{{ access.access_source }}</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono font-semibold ml-4">{{ fmtDollar(access.access_net_revenue) }} net</span>
          <span class="text-xs font-mono opacity-60 ml-2">{{ collapsed.access ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.access" class="p-4 font-mono text-sm">
          <!-- Summary cards -->
          <div class="grid grid-cols-4 gap-3 mb-4">
            <div class="bg-growth-10 rounded p-3 text-center">
              <div class="text-xs text-growth-600">Gross OAP Revenue</div>
              <div class="text-lg font-bold text-growth-900">{{ fmtDollar(access.access_revenue) }}</div>
            </div>
            <div class="bg-growth-10 rounded p-3 text-center">
              <div class="text-xs text-growth-600">Forgone CCM</div>
              <div class="text-lg font-bold text-growth-900">({{ fmtDollar(access.access_forgone_ccm) }})</div>
            </div>
            <div class="bg-growth-10 rounded p-3 text-center">
              <div class="text-xs text-growth-600">Multi-Track Discount</div>
              <div class="text-lg font-bold text-growth-900">({{ fmtDollar(access.multi_track_discount) }})</div>
            </div>
            <div class="bg-emerald-50 rounded p-3 text-center border border-emerald-200">
              <div class="text-xs text-emerald-700">Net ACCESS Revenue</div>
              <div class="text-lg font-bold text-emerald-700">{{ fmtDollar(access.access_net_revenue) }}</div>
            </div>
          </div>

          <!-- By Track -->
          <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">By Clinical Track</div>
          <table class="w-full text-xs mb-4">
            <thead>
              <tr class="text-left text-growth-600 uppercase border-b border-growth-50">
                <th class="pb-1.5 pr-4">Track</th>
                <th class="pb-1.5 pr-4">Conditions</th>
                <th class="pb-1.5 pr-4 text-right">Patients</th>
                <th class="pb-1.5 pr-4 text-right">Rate/yr</th>
                <th class="pb-1.5 text-right">Effective OAP</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="track in ['eckm', 'ckm', 'msk', 'bh']" :key="track" class="border-b border-growth-50">
                <td class="py-1.5 pr-4 font-semibold">{{ access.by_track[track]?.label }}</td>
                <td class="py-1.5 pr-4 text-growth-600">{{ access.by_track[track]?.conditions }}</td>
                <td class="py-1.5 pr-4 text-right">{{ fmtNum(access.by_track[track]?.patients) }}</td>
                <td class="py-1.5 pr-4 text-right">{{ fmtDollar(access.by_track[track]?.initial_rate) }}</td>
                <td class="py-1.5 text-right font-semibold">{{ fmtDollar(access.by_track[track]?.effective_oap) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="border-t-2 border-growth-200 font-semibold">
                <td class="py-1.5" colspan="2">Total</td>
                <td class="py-1.5 text-right">{{ fmtNum(access.total_eligible_patients) }}</td>
                <td></td>
                <td class="py-1.5 text-right">{{ fmtDollar(access.access_revenue) }}</td>
              </tr>
            </tfoot>
          </table>

          <!-- Notes -->
          <div class="text-xs text-growth-400 space-y-1">
            <div>CMS ACCESS Model launches July 2026. OAP = Outcome-Aligned Payment (fixed monthly G-code billing).</div>
            <div>Effective rate = 85% of gross (50% paid monthly + 50% withheld, ~70% recovery assumed).</div>
            <div v-if="access.multi_track_patients">Multi-track overlap: {{ fmtNum(access.multi_track_patients) }} patients in 2+ tracks (5% discount on lower track).</div>
          </div>
        </div>
      </div>

      <!-- STEP 6: PROVIDER UTILIZATION -->
      <div v-if="provUtil" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('provUtil')">
          <span class="text-xs font-mono font-semibold">STEP 6: PROVIDER UTILIZATION</span>
          <span v-if="provUtil.leverage?.staffing" :class="['text-xs px-2 py-0.5 rounded ml-2', provUtil.leverage.staffing === 'balanced' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white']">{{ provUtil.leverage.staffing.replace('_', ' ') }}</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.provUtil ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.provUtil" class="p-4 font-mono text-sm">
          <!-- Physician vs APP comparison -->
          <table class="w-full mb-4">
            <thead>
              <tr class="text-left text-xs text-growth-600 uppercase border-b border-growth-50">
                <th class="pb-1.5 pr-4 w-52"></th>
                <th class="pb-1.5 pr-4 text-right">Physicians</th>
                <th class="pb-1.5 text-right">APPs</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Count</td><td class="py-1.5 text-right font-semibold">{{ provUtil.physician_count }}</td><td class="py-1.5 text-right font-semibold">{{ provUtil.app_count }}</td></tr>
              <tr v-if="provUtil.revenue_per_physician" class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Revenue / Provider</td><td class="py-1.5 text-right">{{ fmtDollar(provUtil.revenue_per_physician) }}</td><td class="py-1.5 text-right">{{ fmtDollar(provUtil.revenue_per_app) }}</td></tr>
              <tr v-if="provUtil.services_per_physician" class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Services / Provider</td><td class="py-1.5 text-right">{{ fmtNum(provUtil.services_per_physician) }}</td><td class="py-1.5 text-right">{{ fmtNum(provUtil.services_per_app) }}</td></tr>
              <tr v-if="provUtil.benes_per_physician" class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Benes / Provider</td><td class="py-1.5 text-right">{{ fmtNum(provUtil.benes_per_physician) }}</td><td class="py-1.5 text-right">{{ fmtNum(provUtil.benes_per_app) }}</td></tr>
              <tr v-if="provUtil.physician_avg_complexity" class="border-b border-growth-50">
                <td class="py-1.5 text-growth-600">Avg E&M Complexity</td>
                <td class="py-1.5 text-right font-semibold">{{ provUtil.physician_avg_complexity }}</td>
                <td class="py-1.5 text-right font-semibold">{{ provUtil.app_avg_complexity }}</td>
              </tr>
              <tr v-if="provUtil.complexity_gap != null" class="border-b border-growth-50 bg-growth-10">
                <td class="py-1.5 text-growth-600">Complexity Gap</td>
                <td class="py-1.5 text-right" colspan="2">
                  <span class="font-semibold">{{ provUtil.complexity_gap }}</span>
                  <span class="text-xs text-growth-400 ml-1">levels</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- E&M distribution side by side -->
          <div v-if="provUtil.physician_em_mix && provUtil.app_em_mix" class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Physician E&M Mix</div>
              <div class="space-y-1">
                <div v-for="code in ['99213', '99214', '99215']" :key="'p'+code" class="flex items-center gap-2 text-xs">
                  <span class="w-12 text-growth-600">{{ code }}</span>
                  <div class="flex-1 bg-growth-10 rounded-full h-3">
                    <div class="bg-blue-400 rounded-full h-3" :style="{ width: provUtil.physician_em_mix[code + '_pct'] + '%' }"></div>
                  </div>
                  <span class="w-10 text-right">{{ provUtil.physician_em_mix[code + '_pct'] }}%</span>
                </div>
              </div>
            </div>
            <div>
              <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">APP E&M Mix</div>
              <div class="space-y-1">
                <div v-for="code in ['99213', '99214', '99215']" :key="'a'+code" class="flex items-center gap-2 text-xs">
                  <span class="w-12 text-growth-600">{{ code }}</span>
                  <div class="flex-1 bg-growth-10 rounded-full h-3">
                    <div class="bg-amber-400 rounded-full h-3" :style="{ width: provUtil.app_em_mix[code + '_pct'] + '%' }"></div>
                  </div>
                  <span class="w-10 text-right">{{ provUtil.app_em_mix[code + '_pct'] }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Leverage assessment -->
          <div v-if="provUtil.leverage" class="space-y-1 text-xs">
            <div v-if="provUtil.leverage.staffing_label" class="flex gap-2"><span class="text-growth-600">Staffing:</span> {{ provUtil.leverage.staffing_label }}</div>
            <div v-if="provUtil.leverage.complexity_label" class="flex gap-2"><span class="text-growth-600">Complexity:</span> {{ provUtil.leverage.complexity_label }}</div>
            <div v-if="provUtil.leverage.productivity_label" class="flex gap-2"><span class="text-growth-600">Productivity:</span> {{ provUtil.leverage.productivity_label }}</div>
            <div v-if="provUtil.leverage.add_apps_net" class="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2">
              Add {{ provUtil.leverage.add_apps }} APP(s): {{ fmtDollar(provUtil.leverage.add_apps_revenue) }} revenue − {{ fmtDollar(provUtil.leverage.add_apps_cost) }} cost = <span class="font-semibold text-emerald-700">{{ fmtDollar(provUtil.leverage.add_apps_net) }} net</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="data.blocks_skipped?.includes('provider_utilization')" class="bg-white rounded-lg border border-growth-50 overflow-hidden opacity-50">
        <div class="px-4 py-2 bg-gray-800 text-white"><span class="text-xs font-mono font-semibold">STEP 6: PROVIDER UTILIZATION</span> <span class="text-xs opacity-60 ml-2">skipped — no per-provider billing data</span></div>
      </div>

      <!-- STEP 7: COST PROFILE -->
      <div v-if="costs" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('costs')">
          <span class="text-xs font-mono font-semibold">STEP 6: COST PROFILE</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.costs ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.costs" class="p-4 font-mono text-sm">
          <div class="bg-growth-10 rounded p-3 mb-4 text-center">
            <div class="text-xs text-growth-600 mb-1">Total Operating Expenses</div>
            <div class="text-2xl font-bold text-growth-900">{{ fmtDollar(costs.total_operating_expenses) }}</div>
            <div class="text-xs text-growth-600 mt-1">{{ fmtDollar(costs.cost_per_visit) }} / visit &middot; {{ fmtDollar(costs.cost_per_provider) }} / provider</div>
          </div>
          <div class="grid grid-cols-2 gap-6">
            <div>
              <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Staff Costs</div>
              <table class="w-full text-sm">
                <tbody>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Physicians</td><td class="py-1 text-right">{{ fmtDollar(costs.physician_costs) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">APPs</td><td class="py-1 text-right">{{ fmtDollar(costs.app_costs) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Admin</td><td class="py-1 text-right">{{ fmtDollar(costs.admin_costs) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Clinical</td><td class="py-1 text-right">{{ fmtDollar(costs.clinical_costs) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Benefits ({{ traceVal('cost_profile', 'benefits_rate') === 'default' ? '25' : '' }}%)</td><td class="py-1 text-right">{{ fmtDollar(costs.benefits) }}</td></tr>
                  <tr class="bg-growth-10"><td class="py-1 font-semibold">Total Staff</td><td class="py-1 text-right font-semibold">{{ fmtDollar(costs.total_staff_costs) }}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Other Costs</div>
              <table class="w-full text-sm">
                <tbody>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Rent ({{ fmtNum(costs.total_sqft) }} sqft)</td><td class="py-1 text-right">{{ fmtDollar(costs.rent_expense) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Malpractice</td><td class="py-1 text-right">{{ fmtDollar(costs.malpractice_expense) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Technology</td><td class="py-1 text-right">{{ fmtDollar(costs.technology_expense) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Supplies</td><td class="py-1 text-right">{{ fmtDollar(costs.supply_expense) }}</td></tr>
                  <tr class="border-b border-growth-50"><td class="py-1 text-growth-600">Billing/RCM</td><td class="py-1 text-right">{{ fmtDollar(costs.billing_expense) }}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 7: RISK & QUALITY -->
      <div v-if="riskQuality" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('risk')">
          <span class="text-xs font-mono font-semibold">STEP 8: RISK &amp; QUALITY</span>
          <span v-if="riskQuality.panel_acuity" :class="['text-xs px-2 py-0.5 rounded ml-2', acuityClass]">{{ riskQuality.panel_acuity }}</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.risk ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.risk" class="p-4 font-mono text-sm">
          <table class="w-full">
            <tbody>
              <tr v-if="riskQuality.weighted_hcc" class="border-b border-growth-50"><td class="py-1.5 text-growth-600 w-52">HCC Risk Score</td><td class="py-1.5 font-semibold">{{ riskQuality.weighted_hcc }}</td><td class="py-1.5 text-right">{{ traceTag('risk_quality', 'weighted_hcc') }}</td></tr>
              <tr v-if="riskQuality.hcc_vs_regional" class="border-b border-growth-50">
                <td class="py-1.5 text-growth-600">vs Regional</td>
                <td class="py-1.5">{{ riskQuality.hcc_vs_regional }}× <span class="text-xs text-growth-400">{{ riskQuality.hcc_vs_regional_label }}</span></td>
                <td></td>
              </tr>
              <tr v-if="riskQuality.panel_acuity" class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Panel Acuity</td><td class="py-1.5 font-semibold">{{ riskQuality.panel_acuity }}</td><td></td></tr>
              <tr v-if="riskQuality.weighted_awv_rate" class="border-b border-growth-50">
                <td class="py-1.5 text-growth-600">AWV Rate</td>
                <td class="py-1.5 font-semibold">{{ (riskQuality.weighted_awv_rate * 100).toFixed(0) }}% <span class="text-xs text-growth-400 font-normal">{{ riskQuality.awv_classification }}</span></td>
                <td class="py-1.5 text-right">{{ traceTag('risk_quality', 'weighted_awv_rate') }}</td>
              </tr>
              <tr v-if="riskQuality.awv_gap > 0" class="border-b border-growth-50">
                <td class="py-1.5 text-growth-600">AWV Gap to 70%</td>
                <td class="py-1.5 text-amber-600">{{ (riskQuality.awv_gap * 100).toFixed(0) }} points</td>
                <td></td>
              </tr>
              <tr v-if="riskQuality.diabetes_burden" class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Diabetes Burden</td><td class="py-1.5">{{ riskQuality.diabetes_burden }}</td><td></td></tr>
              <tr v-if="riskQuality.dual_eligible_level" class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Dual Eligible</td><td class="py-1.5">{{ riskQuality.dual_eligible_level }}</td><td></td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else-if="data.blocks_skipped?.includes('risk_quality')" class="bg-white rounded-lg border border-growth-50 overflow-hidden opacity-50">
        <div class="px-4 py-2 bg-gray-800 text-white"><span class="text-xs font-mono font-semibold">STEP 8: RISK &amp; QUALITY</span> <span class="text-xs opacity-60 ml-2">skipped — no HCC/AWV data</span></div>
      </div>

      <!-- STEP 9: PANEL OPTIMIZATION (Payer Mix) -->
      <div v-if="panelOpt" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('panelOpt')">
          <span class="text-xs font-mono font-semibold">STEP 9: PANEL OPTIMIZATION</span>
          <span v-if="panelOpt.concentration_detail" :class="['text-xs px-2 py-0.5 rounded ml-2', panelOpt.concentration === 'concentrated' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white']">{{ panelOpt.concentration_detail }}</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.panelOpt ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.panelOpt" class="p-4 font-mono text-sm">
          <!-- Blended rate summary -->
          <div class="grid grid-cols-3 gap-3 mb-4">
            <div class="bg-growth-10 rounded p-3 text-center">
              <div class="text-xs text-growth-600">Blended Rate</div>
              <div class="text-xl font-bold text-growth-900">${{ panelOpt.blended_rate }}<span class="text-sm font-normal">/visit</span></div>
            </div>
            <div class="bg-growth-10 rounded p-3 text-center">
              <div class="text-xs text-growth-600">Balanced Practice Avg</div>
              <div class="text-xl font-bold text-growth-600">${{ panelOpt.balanced_blended_rate }}<span class="text-sm font-normal">/visit</span></div>
            </div>
            <div :class="['rounded p-3 text-center', panelOpt.mix_impact >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200']">
              <div class="text-xs" :class="panelOpt.mix_impact >= 0 ? 'text-emerald-700' : 'text-red-700'">Mix Impact</div>
              <div class="text-xl font-bold" :class="panelOpt.mix_impact >= 0 ? 'text-emerald-700' : 'text-red-700'">{{ fmtDollar(panelOpt.mix_impact) }}</div>
            </div>
          </div>

          <!-- Per-payer economics -->
          <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Per-Payer Economics</div>
          <table class="w-full text-xs mb-4">
            <thead>
              <tr class="text-left text-growth-600 uppercase border-b border-growth-50">
                <th class="pb-1.5 pr-4">Payer</th>
                <th class="pb-1.5 pr-4 text-right">Mix</th>
                <th class="pb-1.5 pr-4 text-right">Rate/Visit</th>
                <th class="pb-1.5 pr-4 text-right">Source</th>
                <th class="pb-1.5 pr-4 text-right">Visits</th>
                <th class="pb-1.5 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in payerOrder" :key="'po'+p" class="border-b border-growth-50">
                <td class="py-1 pr-4 capitalize">{{ p.replace('_', ' ') }}</td>
                <td class="py-1 pr-4 text-right font-semibold">{{ panelOpt.payer_economics[p]?.pct }}%</td>
                <td class="py-1 pr-4 text-right">${{ panelOpt.payer_economics[p]?.rate }}</td>
                <td class="py-1 pr-4 text-right text-xs" :class="panelOpt.payer_economics[p]?.rate_source === 'actual' ? 'text-emerald-600' : 'text-growth-400'">{{ panelOpt.payer_economics[p]?.rate_source }}</td>
                <td class="py-1 pr-4 text-right">{{ fmtNum(panelOpt.payer_economics[p]?.visits) }}</td>
                <td class="py-1 text-right font-semibold">{{ fmtDollar(panelOpt.payer_economics[p]?.revenue) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Mix shift scenarios -->
          <div v-if="panelOpt.scenarios?.length" class="mb-2">
            <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Mix Shift Scenarios</div>
            <div class="space-y-2">
              <div v-for="s in panelOpt.scenarios" :key="s.name" class="flex justify-between items-center bg-growth-10 rounded p-2 text-xs">
                <span>{{ s.label }}</span>
                <span class="font-semibold" :class="s.delta > 0 ? 'text-emerald-600' : 'text-red-600'">{{ s.delta > 0 ? '+' : '' }}{{ fmtDollar(s.delta) }} (→ ${{ s.new_blended_rate }}/visit)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="data.blocks_skipped?.includes('panel_optimization')" class="bg-white rounded-lg border border-growth-50 overflow-hidden opacity-50">
        <div class="px-4 py-2 bg-gray-800 text-white"><span class="text-xs font-mono font-semibold">STEP 9: PANEL OPTIMIZATION</span> <span class="text-xs opacity-60 ml-2">skipped — no visit data</span></div>
      </div>

      <!-- STEP 10: CARE MANAGEMENT REVENUE -->
      <div v-if="careMgmt" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('careMgmt')">
          <span class="text-xs font-mono font-semibold">STEP 10: CARE MANAGEMENT REVENUE</span>
          <span v-if="careMgmt.care_mgmt_net > 0" class="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded ml-2">{{ fmtDollar(careMgmt.care_mgmt_net) }} net margin</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.careMgmt ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.careMgmt" class="p-4 font-mono text-sm space-y-4">

          <!-- Provider Economics Context -->
          <div v-if="careMgmt.provider_economics" class="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
            <div class="font-semibold text-amber-800 mb-1">Provider Time vs Care Manager Time</div>
            <div class="grid grid-cols-3 gap-3 mb-2">
              <div><span class="text-growth-600">E/M (Medicare):</span> <span class="font-semibold">{{ fmtDollar(careMgmt.provider_economics.em_revenue_per_hour?.medicare) }}/hr</span></div>
              <div><span class="text-growth-600">E/M (Commercial):</span> <span class="font-semibold">{{ fmtDollar(careMgmt.provider_economics.em_revenue_per_hour?.commercial) }}/hr</span></div>
              <div><span class="text-growth-600">CM Cost:</span> <span class="font-semibold">{{ fmtDollar(careMgmt.provider_economics.cm_cost_per_hour) }}/hr</span></div>
            </div>
            <div class="text-amber-700 italic">{{ careMgmt.provider_economics.message }}</div>
          </div>

          <!-- Eligible Population -->
          <div v-if="careMgmt.eligible_population">
            <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Eligible Population</div>
            <table class="w-full">
              <tbody>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 w-52">Total Beneficiaries</td><td class="py-1.5 text-right font-semibold">{{ fmtNum(careMgmt.eligible_population.total_benes) }}</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Chronic Patients (2+)</td><td class="py-1.5 text-right">{{ fmtNum(careMgmt.eligible_population.chronic_patients) }} <span class="text-xs text-growth-400">{{ (careMgmt.eligible_population.chronic_rate * 100).toFixed(0) }}%</span></td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">RPM Eligible</td><td class="py-1.5 text-right">{{ fmtNum(careMgmt.eligible_population.rpm_eligible) }}</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Monthly Discharges</td><td class="py-1.5 text-right">{{ fmtNum(careMgmt.eligible_population.monthly_discharges) }}</td></tr>
                <tr class="border-b border-growth-50 bg-growth-10"><td class="py-1.5 text-growth-600">Enrollment Target (30%)</td><td class="py-1.5 text-right font-semibold">{{ fmtNum(careMgmt.eligible_population.enrollment_target) }}</td></tr>
                <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600">Currently Enrolled</td><td class="py-1.5 text-right">{{ fmtNum(careMgmt.eligible_population.currently_enrolled) }}</td></tr>
                <tr class="bg-growth-10"><td class="py-1.5 font-semibold">Untapped Pool</td><td class="py-1.5 text-right font-bold text-growth-tangerine">{{ fmtNum(careMgmt.eligible_population.untapped_pool) }}</td></tr>
              </tbody>
            </table>
          </div>

          <!-- CM Capacity -->
          <div v-if="careMgmt.cm_capacity">
            <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Care Manager Capacity</div>
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-1 text-xs">
                <div><span class="text-growth-600">Program:</span> <span class="font-semibold uppercase">{{ careMgmt.cm_capacity.program }}</span></div>
                <div><span class="text-growth-600">Minutes/pt/month:</span> {{ careMgmt.cm_capacity.minutes_per_patient }} <span class="text-growth-400">({{ careMgmt.cm_capacity.blended_mppm_with_rpm }} with RPM)</span></div>
                <div><span class="text-growth-600">Panel per CM:</span> <span class="font-semibold">{{ careMgmt.cm_capacity.effective_panel_per_cm }}</span> patients</div>
              </div>
              <div class="space-y-1 text-xs">
                <div><span class="text-growth-600">RPM per CM:</span> {{ careMgmt.cm_capacity.rpm_per_cm }} patients</div>
                <div><span class="text-growth-600">Monthly attrition:</span> {{ (careMgmt.cm_capacity.monthly_attrition_rate * 100).toFixed(1) }}%</div>
                <div><span class="text-growth-600">New enrollments needed:</span> {{ careMgmt.cm_capacity.new_enrollments_needed_monthly }}/mo</div>
              </div>
            </div>
          </div>

          <!-- Staffing Scenarios -->
          <div v-if="careMgmt.cm_scenarios?.length">
            <div class="text-xs text-growth-600 uppercase tracking-wide mb-2">Staffing Scenarios</div>
            <div class="space-y-3">
              <div v-for="s in careMgmt.cm_scenarios" :key="s.cm_count" class="border border-growth-50 rounded-lg overflow-hidden">
                <div class="px-4 py-2 bg-growth-10 flex justify-between items-center">
                  <span class="font-semibold text-growth-900">{{ s.cm_count }} Care Manager{{ s.cm_count > 1 ? 's' : '' }} ({{ s.program }})</span>
                  <span class="font-bold" :class="s.steady_state.margin > 0 ? 'text-emerald-700' : 'text-red-700'">{{ fmtDollar(s.steady_state.margin) }} margin</span>
                </div>
                <div class="px-4 py-2 text-xs">
                  <div class="grid grid-cols-2 gap-x-6 gap-y-1">
                    <div><span class="text-growth-600">Annual Cost:</span> {{ fmtDollar(s.annual_cost) }}</div>
                    <div><span class="text-growth-600">Break-even:</span> Month {{ s.break_even_month || '—' }}</div>
                    <div><span class="text-growth-600">Year 1 Revenue:</span> {{ fmtDollar(s.year_1.revenue) }} <span class="text-growth-400">(with ramp)</span></div>
                    <div><span class="text-growth-600">Year 1 Margin:</span> <span :class="s.year_1.margin > 0 ? 'text-emerald-600' : 'text-red-600'">{{ fmtDollar(s.year_1.margin) }}</span></div>
                    <div><span class="text-growth-600">Steady Revenue:</span> {{ fmtDollar(s.steady_state.revenue) }}</div>
                    <div><span class="text-growth-600">ROI:</span> <span class="font-semibold">{{ s.roi_pct }}%</span></div>
                  </div>
                  <div class="mt-2 flex gap-4">
                    <div v-for="(val, key) in s.steady_state.revenue_by_service" :key="key">
                      <span class="text-growth-600 uppercase">{{ key }}:</span> {{ fmtDollar(val) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Program Comparison -->
          <div v-if="careMgmt.program_comparison" class="text-xs">
            <div class="text-growth-600 uppercase tracking-wide mb-1">Program Comparison (1 CM)</div>
            <div class="grid grid-cols-2 gap-3">
              <div class="border border-growth-50 rounded p-2">
                <div class="font-semibold mb-1">{{ careMgmt.program_comparison.selected.program }}</div>
                <div>Revenue: {{ fmtDollar(careMgmt.program_comparison.selected.steady_state.revenue) }}</div>
                <div>Margin: <span class="font-semibold text-emerald-600">{{ fmtDollar(careMgmt.program_comparison.selected.steady_state.margin) }}</span></div>
              </div>
              <div class="border border-growth-50 rounded p-2">
                <div class="font-semibold mb-1">{{ careMgmt.program_comparison.alternate.program }}</div>
                <div>Revenue: {{ fmtDollar(careMgmt.program_comparison.alternate.steady_state.revenue) }}</div>
                <div>Margin: <span class="font-semibold text-emerald-600">{{ fmtDollar(careMgmt.program_comparison.alternate.steady_state.margin) }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="data.blocks_skipped?.includes('care_management_revenue')" class="bg-white rounded-lg border border-growth-50 overflow-hidden opacity-50">
        <div class="px-4 py-2 bg-gray-800 text-white"><span class="text-xs font-mono font-semibold">STEP 10: CARE MANAGEMENT REVENUE</span> <span class="text-xs opacity-60 ml-2">skipped — no beneficiary data</span></div>
      </div>

      <!-- STEP 11: P&L STATEMENT -->
      <div v-if="pl" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('pl')">
          <span class="text-xs font-mono font-semibold">STEP 11: P&amp;L STATEMENT</span>
          <span :class="['text-xs px-2 py-0.5 rounded ml-2', pl.net_income >= 0 ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white']">{{ pl.net_margin }}% margin</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.pl ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.pl" class="p-4 font-mono text-sm">
          <table class="w-full">
            <tbody>
              <tr class="border-b border-growth-50 bg-growth-10"><td class="py-1.5 font-semibold w-52">Total Revenue</td><td class="py-1.5 font-semibold text-right">{{ fmtDollar(pl.total_revenue) }}</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">FFS Collections</td><td class="py-1.5 text-right">{{ fmtDollar(pl.ffs_revenue) }} <span class="text-xs text-growth-400">{{ pl.revenue_composition?.ffs_pct }}%</span></td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">VBC Revenue</td><td class="py-1.5 text-right">{{ fmtDollar(pl.vbc_revenue) }} <span class="text-xs text-growth-400">{{ pl.revenue_composition?.vbc_pct }}%</span></td></tr>
              <tr v-if="pl.access_revenue" class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">ACCESS Revenue</td><td class="py-1.5 text-right">{{ fmtDollar(pl.access_revenue) }} <span class="text-xs text-growth-400">{{ pl.revenue_composition?.access_pct }}%</span></td></tr>
              <tr v-if="pl.care_mgmt_net" class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">Care Mgmt Net</td><td class="py-1.5 text-right">{{ fmtDollar(pl.care_mgmt_net) }} <span class="text-xs text-growth-400">{{ pl.revenue_composition?.care_mgmt_pct }}%</span></td></tr>
              <tr class="border-b border-growth-50 bg-growth-10"><td class="py-1.5 font-semibold">Operating Expenses</td><td class="py-1.5 font-semibold text-right">({{ fmtDollar(pl.total_operating_expenses) }})</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">Staff</td><td class="py-1.5 text-right">({{ fmtDollar(pl.total_staff_costs) }})</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">Facility</td><td class="py-1.5 text-right">({{ fmtDollar(pl.total_facility_costs) }})</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">Provider Costs</td><td class="py-1.5 text-right">({{ fmtDollar(pl.total_provider_costs) }})</td></tr>
              <tr class="border-b border-growth-50"><td class="py-1.5 text-growth-600 pl-4">Variable</td><td class="py-1.5 text-right">({{ fmtDollar(pl.total_variable_costs) }})</td></tr>
              <tr class="border-t-2 border-growth-50" :class="pl.ebitda >= 0 ? 'bg-emerald-50' : 'bg-red-50'">
                <td class="py-2 font-bold">EBITDA</td>
                <td class="py-2 text-right font-bold" :class="pl.ebitda >= 0 ? 'text-emerald-700' : 'text-red-700'">{{ fmtDollar(pl.ebitda) }} <span class="text-xs font-normal">{{ pl.ebitda_margin }}%</span></td>
              </tr>
              <tr :class="pl.net_income >= 0 ? 'bg-emerald-50' : 'bg-red-50'">
                <td class="py-2 font-bold">Net Income</td>
                <td class="py-2 text-right font-bold" :class="pl.net_income >= 0 ? 'text-emerald-700' : 'text-red-700'">{{ fmtDollar(pl.net_income) }} <span class="text-xs font-normal">{{ pl.net_margin }}%</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- STEP 12: OPTIMIZATION -->
      <div v-if="opt" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('opt')">
          <span class="text-xs font-mono font-semibold">STEP 12: OPTIMIZATION</span>
          <span class="text-xs bg-growth-tangerine text-white px-2 py-0.5 rounded ml-2">{{ fmtDollar(opt.total_opportunity) }} opportunity</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.opt ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.opt" class="p-4 font-mono text-sm space-y-3">
          <div v-for="lever in opt.levers" :key="lever.name" class="border border-growth-50 rounded-lg overflow-hidden">
            <div class="px-4 py-2 bg-growth-10 flex justify-between items-center">
              <div>
                <span class="font-semibold text-growth-900">{{ lever.label }}</span>
                <span :class="['text-xs px-1.5 py-0.5 rounded ml-2', lever.signal === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700']">{{ lever.signal }}</span>
              </div>
              <span class="font-bold text-growth-tangerine text-lg">{{ fmtDollar(lever.impact) }}</span>
            </div>
            <div class="px-4 py-2 text-xs space-y-1">
              <div><span class="text-growth-600">Current:</span> {{ lever.current }}</div>
              <div><span class="text-growth-600">Target:</span> {{ lever.target }}</div>
              <div class="text-growth-600 italic">{{ lever.narrative }}</div>
            </div>
          </div>
          <div class="bg-growth-10 rounded p-3 flex justify-between items-center">
            <span class="font-semibold text-growth-900">Total Opportunity ({{ opt.lever_count }} levers)</span>
            <span class="font-bold text-growth-tangerine text-xl">{{ fmtDollar(opt.total_opportunity) }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="data.blocks_skipped?.includes('optimization')" class="bg-white rounded-lg border border-growth-50 overflow-hidden opacity-50">
        <div class="px-4 py-2 bg-gray-800 text-white"><span class="text-xs font-mono font-semibold">STEP 12: OPTIMIZATION</span> <span class="text-xs opacity-60 ml-2">skipped — insufficient data for levers</span></div>
      </div>

      <!-- STEP 13: FORECAST -->
      <div v-if="fc" class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-800 text-white flex justify-between items-center cursor-pointer" @click="toggle('forecast')">
          <span class="text-xs font-mono font-semibold">STEP 13: FORECAST</span>
          <span class="flex-1"></span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.forecast ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.forecast" class="p-4 font-mono text-sm">
          <div class="text-xs text-growth-600 mb-3">
            FFS growth {{ fc.ffs_growth_rate }}% &middot; Cost growth {{ fc.cost_growth_rate }}% &middot; Optimization capture {{ fc.optimization_capture_rate }}%<template v-if="fc.optimization_capture_rate"> of {{ fmtDollar(fc.annual_optimization_target * 100 / fc.optimization_capture_rate) }}</template>
          </div>
          <table class="w-full text-xs">
            <thead>
              <tr class="text-left text-growth-600 uppercase border-b border-growth-50">
                <th class="pb-1.5 pr-4">Year</th>
                <th class="pb-1.5 pr-4 text-right">Baseline Revenue</th>
                <th class="pb-1.5 pr-4 text-right">Costs</th>
                <th class="pb-1.5 pr-4 text-right">Baseline Income</th>
                <th class="pb-1.5 pr-4 text-right">+ Optimization</th>
                <th class="pb-1.5 text-right">Optimized Income</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="yr in fc.years" :key="yr.year" :class="['border-b border-growth-50', yr.year === 0 ? 'bg-growth-10' : '']">
                <td class="py-1.5 pr-4">{{ yr.year === 0 ? 'Current' : 'Year ' + yr.year }}</td>
                <td class="py-1.5 pr-4 text-right">{{ fmtDollar(yr.baseline_revenue) }}</td>
                <td class="py-1.5 pr-4 text-right">({{ fmtDollar(yr.baseline_costs) }})</td>
                <td class="py-1.5 pr-4 text-right" :class="yr.baseline_income >= 0 ? 'text-emerald-600' : 'text-red-600'">{{ fmtDollar(yr.baseline_income) }}</td>
                <td class="py-1.5 pr-4 text-right text-growth-tangerine">{{ yr.optimization_revenue > 0 ? '+' + fmtDollar(yr.optimization_revenue) : '—' }}</td>
                <td class="py-1.5 text-right font-semibold" :class="yr.optimized_income >= 0 ? 'text-emerald-600' : 'text-red-600'">{{ fmtDollar(yr.optimized_income) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="mt-3 bg-growth-10 rounded p-3 flex justify-between text-sm">
            <span class="text-growth-600">Year 5 delta (optimized vs baseline):</span>
            <span class="font-bold text-growth-tangerine">+{{ fmtDollar(fc.year_5_delta) }}</span>
          </div>
        </div>
      </div>

      <!-- TRACE DUMP -->
      <div class="bg-white rounded-lg border border-growth-50 overflow-hidden">
        <div class="px-4 py-2 border-b border-growth-50 bg-gray-700 text-white flex justify-between items-center cursor-pointer" @click="toggle('trace')">
          <span class="text-xs font-mono font-semibold">TRACE</span>
          <span class="text-xs font-mono opacity-60">{{ collapsed.trace ? '▸' : '▾' }}</span>
        </div>
        <div v-show="!collapsed.trace" class="p-4">
          <div class="grid grid-cols-1 gap-4">
            <div v-for="(bt, name) in data.trace" :key="name">
              <div class="text-xs font-mono font-semibold text-growth-900 mb-1">{{ name }}</div>
              <div class="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs font-mono">
                <template v-for="(source, param) in bt" :key="param">
                  <div class="text-growth-600 truncate">{{ param }}</div>
                  <div :class="traceColor(source)">{{ source }}</div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Blocks ran/skipped -->
      <div class="flex gap-4 text-xs font-mono text-growth-600">
        <span>Blocks ran: {{ data.blocks_ran?.join(', ') }}</span>
        <span v-if="data.blocks_skipped?.length">Skipped: {{ data.blocks_skipped.join(', ') }}</span>
      </div>

    </template>

    <!-- No data -->
    <div v-else-if="!loading" class="bg-white rounded-lg border border-growth-50 p-6 text-center text-growth-600 text-sm">
      <p>Financial assessment unavailable</p>
      <p class="text-xs mt-1">Practice may not have linked TIN or claims data</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PracticeFinancial',
  props: {
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      collapsed: {
        profile: false,
        panel: false,
        ffs: false,
        vbc: false,
        access: false,
        provUtil: false,
        costs: false,
        risk: false,
        panelOpt: false,
        careMgmt: false,
        pl: false,
        opt: false,
        forecast: false,
        trace: true,
      },
    }
  },
  computed: {
    profile() { return this.data?.results?.practice_profile },
    panel() { return this.data?.results?.patient_panel },
    ffs() { return this.data?.results?.ffs_revenue },
    vbc() { return this.data?.results?.vbc_revenue },
    access() { return this.data?.results?.access_revenue },
    provUtil() { return this.data?.results?.provider_utilization },
    costs() { return this.data?.results?.cost_profile },
    riskQuality() { return this.data?.results?.risk_quality },
    panelOpt() { return this.data?.results?.panel_optimization },
    careMgmt() { return this.data?.results?.care_management_revenue },
    pl() { return this.data?.results?.pl_statement },
    opt() { return this.data?.results?.optimization },
    fc() { return this.data?.results?.forecast },
    payerOrder() { return ['medicare', 'medicaid', 'commercial', 'self_pay'] },
    trackOrder() { return ['eckm', 'ckm', 'msk', 'bh'] },
    acuityClass() {
      const a = this.riskQuality?.panel_acuity
      if (a === 'complex') return 'bg-red-500 text-white'
      if (a === 'high') return 'bg-amber-500 text-white'
      if (a === 'moderate') return 'bg-blue-500 text-white'
      return 'bg-gray-500 text-white'
    },
    qualityClass() {
      const q = this.data?.data_quality
      if (q === 'rich') return 'bg-emerald-100 text-emerald-700'
      if (q === 'moderate') return 'bg-amber-100 text-amber-700'
      return 'bg-gray-100 text-gray-600'
    },
    providedCount() {
      if (!this.data?.trace) return 0
      return Object.values(this.data.trace).reduce((sum, bt) => sum + Object.values(bt).filter(v => v === 'provided').length, 0)
    },
    totalCount() {
      if (!this.data?.trace) return 0
      return Object.values(this.data.trace).reduce((sum, bt) => sum + Object.keys(bt).length, 0)
    },
  },
  methods: {
    toggle(key) { this.collapsed[key] = !this.collapsed[key] },
    fmtNum(n) {
      if (n == null) return '—'
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return Math.round(n).toLocaleString()
      return String(n)
    },
    fmtDollar(n) {
      if (!n && n !== 0) return '—'
      if (n === 0) return '$0'
      if (Math.abs(n) >= 1000000000) return '$' + (n / 1000000000).toFixed(2) + 'B'
      if (Math.abs(n) >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M'
      if (Math.abs(n) >= 1000) return '$' + Math.round(n).toLocaleString()
      return '$' + Math.round(n).toLocaleString()
    },
    fmtExact(n) {
      if (n == null) return '—'
      return Math.round(n).toLocaleString()
    },
    traceTag(block, param) {
      const source = this.data?.trace?.[block]?.[param]
      if (!source) return ''
      if (source === 'provided') return 'provided'
      if (source.startsWith('from:')) return source
      if (source === 'default') return 'default'
      return source
    },
    traceVal(block, param) {
      return this.data?.trace?.[block]?.[param] || ''
    },
    traceColor(source) {
      if (source === 'provided') return 'text-emerald-600'
      if (source?.startsWith('from:')) return 'text-blue-600'
      if (source === 'default') return 'text-growth-400'
      if (source === 'missing') return 'text-red-600'
      return 'text-growth-600'
    },
    pathClass(source) {
      if (source === 'rich' || source === 'actual' || source === 'partial_actuals') return 'bg-emerald-500 text-white'
      if (source === 'moderate' || source === 'simple' || source === 'waterfall' || source === 'projected') return 'bg-amber-500 text-white'
      return 'bg-gray-500 text-white'
    },
  },
}
</script>
